<script lang="ts">
  import { defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'

  export default defineComponent({
    name: 'TableContainer',

    setup() {
      const topScroll = ref<HTMLElement | null>(null)
      const body = ref<HTMLElement | null>(null)
      const scrollWidth = ref(0)
      const overflows = ref(false)
      let resizeObserver: ResizeObserver | null = null
      let mutationObserver: MutationObserver | null = null

      const measure = () => {
        if (!body.value) return

        scrollWidth.value = body.value.scrollWidth
        overflows.value = body.value.scrollWidth > body.value.clientWidth
      }

      const observeContent = () => {
        if (!body.value || !resizeObserver) return

        resizeObserver.disconnect()
        resizeObserver.observe(body.value)
        Array.from(body.value.children).forEach((child) =>
          resizeObserver.observe(child),
        )
        measure()
      }

      const syncFromTop = () => {
        if (body.value && topScroll.value) {
          body.value.scrollLeft = topScroll.value.scrollLeft
        }
      }

      const syncFromBody = () => {
        if (body.value && topScroll.value) {
          topScroll.value.scrollLeft = body.value.scrollLeft
        }
      }

      onMounted(() => {
        if (!body.value) return

        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(measure)
        }

        mutationObserver = new MutationObserver(observeContent)
        mutationObserver.observe(body.value, { childList: true })
        observeContent()
      })

      onBeforeUnmount(() => {
        resizeObserver?.disconnect()
        mutationObserver?.disconnect()
      })

      return {
        topScroll,
        body,
        scrollWidth,
        overflows,
        syncFromTop,
        syncFromBody,
      }
    },
  })
</script>

<template>
  <div class="table-container">
    <div
      v-show="overflows"
      ref="topScroll"
      class="table-container__top-scroll"
      aria-hidden="true"
      @scroll="syncFromTop"
    >
      <div :style="{ width: `${scrollWidth}px` }" />
    </div>

    <div ref="body" class="table-container__body" @scroll="syncFromBody">
      <slot />
    </div>
  </div>
</template>

<style lang="sass">
  @use '@/variables' as *

  .table-container
    display: block
    width: 100%

    &__top-scroll,
    &__body
      overflow-x: auto

    &__top-scroll
      overflow-y: hidden

      > div
        height: 1px

    table
      width: 100%
      max-width: 100%

      thead
        th
          text-align: left
          text-transform: uppercase
          font-size: $font-size-xs
          font-weight: $font-weight-semibold
          letter-spacing: .04em
          color: $text-muted
          padding: 1rem 1.25rem
          border-bottom: 1px solid $border-color-strong

      tbody,
      tfoot
        tr
          td
            padding: 1rem 1.25rem
            white-space: nowrap
            color: $text-color
            text-decoration: none
            border-bottom: 1px solid $border-color

          &:last-child
            td
              border-bottom: 0

          &:hover
            td
              cursor: pointer
              background-color: $neutral-100

      tfoot
        tr
          td
            border-top: 1px solid $border-color-heavy
</style>
